// src/views/admin/PushNotificationPage.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Notification Command Center (FlowSense Notify)
//
// --- UPDATE: rebuilt on FlowSense Notify (Firebase removed).
//   Compose     audience builder (roles incl. inspectors + contractors, groups, teams, people),
//               templates with variables, priority / topic / channels, acknowledgement +
//               escalation, link, schedule, expiry — with a live FlowSense AI forecast
//   History     every dispatch (console, automation, SLA AI, watchdog, dispatch AI) with delivery /
//               read / ack funnel, per-person status, reminders
//   Scheduled   pending sends: reschedule / cancel
//   Templates   built-in + your own (CRUD)
//   AI Insights read rates by topic / role / hour, fatigue, ack compliance, recommendations
//   Public Broadcast / News & Alerts — existing PublicBroadcastForm + NewsFeedManager kept,
//               plus the instant citizen alert (live + browser push)
// Fixes from the old page: history was a stub, scheduling never sent, multi-user sends looped one
// request per user, templates were "coming soon", targeting was techs only, and TemplateCard
// called useColorModeValue inside JSX (hook-order bug).
// --- END UPDATE
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, Heading, HStack, Icon, IconButton, Select, Skeleton,
    Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Tooltip, useColorModeValue, useDisclosure, VStack,
} from '@chakra-ui/react';
import { MdNotificationsActive, MdRefresh, MdSettings } from 'react-icons/md';

import * as api from '../../services/notifyApi';
import notifyStream from '../../services/notifyStream';
import NotifyStatsStrip from '../../components/notify/console/NotifyStatsStrip';
import NotifyComposer from '../../components/notify/console/NotifyComposer';
import NotifyHistoryTable from '../../components/notify/console/NotifyHistoryTable';
import NotifyTemplatesPanel from '../../components/notify/console/NotifyTemplatesPanel';
import NotifyInsightsPanel from '../../components/notify/console/NotifyInsightsPanel';
import NotifyPublicBroadcastPanel from '../../components/notify/console/NotifyPublicBroadcastPanel';
import NotificationPreferences from '../../components/notify/NotificationPreferences';
import PublicBroadcastForm from '../../components/notifications/PublicBroadcastForm';
import NewsFeedManager from '../../components/notifications/NewsFeedManager';

const TABS = ['compose', 'history', 'scheduled', 'templates', 'insights', 'public', 'news'];
const STATUS_BADGE = {
    live: { scheme: 'green', label: 'Live' }, connecting: { scheme: 'yellow', label: 'Connecting' }, reconnecting: { scheme: 'orange', label: 'Reconnecting' },
    polling: { scheme: 'blue', label: 'Polling' }, offline: { scheme: 'red', label: 'Offline' }, idle: { scheme: 'gray', label: 'Idle' },
};

const initialTab = () => {
    try {
        const t = new URLSearchParams(window.location.search).get('tab');
        const i = TABS.indexOf(t);
        return i >= 0 ? i : 0;
    } catch (_) { return 0; }
};

export default function PushNotificationPage() {
    const pageBg = useColorModeValue('gray.50', 'gray.900');
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const gradient = useColorModeValue('linear(to-r, purple.600, teal.500)', 'linear(to-r, purple.300, teal.300)');
    const prefs = useDisclosure();

    const [tab, setTab] = useState(initialTab);
    const [days, setDays] = useState(7);
    const [catalog, setCatalog] = useState({ loading: true, data: null, error: null });
    const [stats, setStats] = useState({ loading: true, data: null, error: null });
    const [templates, setTemplates] = useState({ loading: true, data: [] });
    const [presetTemplate, setPresetTemplate] = useState(null);
    const [historyKey, setHistoryKey] = useState(0);
    const [historyStatus, setHistoryStatus] = useState('');
    const [liveStatus, setLiveStatus] = useState(notifyStream.getStatus());
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const loadCatalog = useCallback(async (signal) => {
        setCatalog(c => ({ ...c, loading: true, error: null }));
        try {
            const data = await api.getCatalog(signal);
            if (isMounted.current) setCatalog({ loading: false, data, error: null });
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setCatalog({ loading: false, data: null, error: e });
        }
    }, []);

    const loadStats = useCallback(async (signal) => {
        setStats(s => ({ ...s, loading: !s.data, error: null }));
        try {
            const data = await api.getStats(days, signal);
            if (isMounted.current) setStats({ loading: false, data, error: null });
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setStats(s => ({ ...s, loading: false, error: e }));
        }
    }, [days]);

    const loadTemplates = useCallback(async (signal) => {
        try {
            const data = await api.listTemplates(signal);
            if (isMounted.current) setTemplates({ loading: false, data: data || [] });
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setTemplates(t => ({ ...t, loading: false }));
        }
    }, []);

    useEffect(() => { const c = new AbortController(); loadCatalog(c.signal); return () => c.abort(); }, [loadCatalog]);
    const canCompose = Boolean(catalog.data?.permissions?.compose);
    useEffect(() => {
        if (!canCompose) return undefined;
        const c = new AbortController();
        loadStats(c.signal);
        return () => c.abort();
    }, [canCompose, loadStats]);
    useEffect(() => {
        if (!canCompose) return undefined;
        const c = new AbortController();
        loadTemplates(c.signal);
        return () => c.abort();
    }, [canCompose, loadTemplates]);

    // Live: dispatch results refresh the KPIs (debounced) — the stream is shared with the bell
    useEffect(() => {
        const stop = notifyStream.start();
        let t = null;
        const offs = [
            notifyStream.on('status', (s) => { if (isMounted.current) setLiveStatus(s); }),
            notifyStream.on('dispatch_update', () => { clearTimeout(t); t = setTimeout(() => loadStats(), 800); }),
        ];
        setLiveStatus(notifyStream.getStatus());
        return () => { offs.forEach(o => o()); stop(); clearTimeout(t); };
    }, [loadStats]);

    const onTab = useCallback((i) => {
        setTab(i);
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('tab', TABS[i]);
            window.history.replaceState(window.history.state, '', url.toString());
        } catch (_) { /* ignore */ }
    }, []);

    const onSent = useCallback(() => { loadStats(); setHistoryKey(k => k + 1); }, [loadStats]);
    const applyLibraryTemplate = useCallback((t) => { setPresetTemplate({ ...t, _at: Date.now() }); onTab(0); }, [onTab]);
    const showHistory = useCallback((status) => { setHistoryStatus(status); onTab(status === 'scheduled' ? 2 : 1); }, [onTab]);

    const perms = catalog.data?.permissions || {};
    const live = STATUS_BADGE[liveStatus] || STATUS_BADGE.idle;

    return (
        <Box bg={pageBg} minH="100%" p={{ base: 3, md: 6 }} pt={{ base: '120px', md: '90px' }}>
            <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={5} gap={3} direction={{ base: 'column', md: 'row' }}>
                <HStack spacing={3}>
                    <Flex bgGradient={gradient} p={3} borderRadius="xl" color="white"><Icon as={MdNotificationsActive} boxSize={7} /></Flex>
                    <Box>
                        <Heading size="lg" bgGradient={gradient} bgClip="text">Notification Command Center</Heading>
                        <Text fontSize="sm" color={muted}>FlowSense Notify — FlowSense’s own notification system: live on the web and tech app, browser push and email. No Firebase.</Text>
                    </Box>
                </HStack>
                <HStack>
                    <Tooltip label="Live connection to FlowSense Notify" hasArrow><Badge colorScheme={live.scheme} px={2} py={1} borderRadius="full">● {live.label}</Badge></Tooltip>
                    {canCompose && (
                        <Select size="sm" w="130px" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="KPI period" bg={panel}>
                            {[1, 7, 30, 90].map(n => <option key={n} value={n}>{n === 1 ? 'Last 24 h' : `Last ${n} days`}</option>)}
                        </Select>
                    )}
                    <Tooltip label="Refresh" hasArrow><IconButton size="sm" icon={<MdRefresh />} aria-label="Refresh" onClick={() => { loadStats(); setHistoryKey(k => k + 1); loadTemplates(); }} /></Tooltip>
                    <Button size="sm" leftIcon={<MdSettings />} onClick={prefs.onOpen}>My settings</Button>
                </HStack>
            </Flex>

            {catalog.error ? (
                <Alert status="error" borderRadius="xl"><AlertIcon /><AlertDescription flex="1">{catalog.error.message}</AlertDescription><Button size="sm" onClick={() => loadCatalog()}>Retry</Button></Alert>
            ) : catalog.loading ? (
                <Stack spacing={4}><Skeleton h="90px" borderRadius="xl" /><Skeleton h="420px" borderRadius="2xl" /></Stack>
            ) : !canCompose ? (
                <Alert status="warning" borderRadius="xl">
                    <AlertIcon />
                    <AlertDescription>Sending notifications is limited to office staff, supervisors, managers and admins. Your own notifications are in the bell at the top of the page.</AlertDescription>
                </Alert>
            ) : (
                <VStack align="stretch" spacing={5}>
                    {stats.error && <Alert status="warning" borderRadius="xl"><AlertIcon /><AlertDescription flex="1">KPIs unavailable — {stats.error.message}</AlertDescription><Button size="sm" onClick={() => loadStats()}>Retry</Button></Alert>}
                    <NotifyStatsStrip stats={stats.data} loading={stats.loading} onShowScheduled={() => showHistory('scheduled')} onShowFailed={() => showHistory('failed')} />

                    <Tabs index={tab} onChange={onTab} variant="soft-rounded" colorScheme="purple" isLazy>
                        <TabList bg={panel} p={2} borderRadius="2xl" borderWidth="1px" borderColor={border} mb={4} overflowX="auto">
                            <Tab>Compose</Tab>
                            <Tab>History</Tab>
                            <Tab>Scheduled{stats.data?.scheduled ? <Badge ml={2} colorScheme="blue">{stats.data.scheduled}</Badge> : null}</Tab>
                            <Tab>Templates</Tab>
                            <Tab>AI Insights</Tab>
                            <Tab>Public Broadcast</Tab>
                            <Tab>News &amp; Alerts</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel p={0}>
                                <NotifyComposer permissions={perms} templates={templates.data} presetTemplate={presetTemplate} onSent={onSent} />
                            </TabPanel>
                            <TabPanel p={0}><NotifyHistoryTable presetStatus={historyStatus === 'scheduled' ? '' : historyStatus} refreshKey={historyKey} onChanged={() => loadStats()} /></TabPanel>
                            <TabPanel p={0}><NotifyHistoryTable presetStatus="scheduled" refreshKey={historyKey} onChanged={() => loadStats()} /></TabPanel>
                            <TabPanel p={0}><NotifyTemplatesPanel templates={templates.data} loading={templates.loading} canEdit={perms.edit_templates} onUse={applyLibraryTemplate} onChanged={() => loadTemplates()} /></TabPanel>
                            <TabPanel p={0}><NotifyInsightsPanel /></TabPanel>
                            <TabPanel p={0}>
                                <Stack spacing={4}>
                                    {perms.public_broadcast && <NotifyPublicBroadcastPanel />}
                                    <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                                        <PublicBroadcastForm />
                                    </Box>
                                </Stack>
                            </TabPanel>
                            <TabPanel p={0}>
                                <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                                    <NewsFeedManager />
                                </Box>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </VStack>
            )}
            {prefs.isOpen && <NotificationPreferences isOpen={prefs.isOpen} onClose={prefs.onClose} />}
        </Box>
    );
}
